import { Suspense, useEffect, useMemo, useState } from "react";

import { ApplicationLayout as CoreApplicationLayout, ErrorBoundary, TrackNavigation, PageLoading } from "am-tax-fe-core";
import { Outlet, useLocation } from "react-router-dom";

import { useBanners } from "../api/admin/api-queries.ts";
import { useCurrentUser } from "../api/apiQueries.ts";
import { GlobalEngagePopupBannerProps, GlobalEngagePopupModal } from "../components/GlobalEngageBanner/GlobalEngagePopupModal.tsx";
import { LeftNav } from "../components/LeftNav/LeftNav.tsx";
import { ScrollToTop } from "../components/ScrollToTop";

import { Page } from "./Page.tsx";

function ApplicationLayout() {
    const location = useLocation();

    const { data: currentUser } = useCurrentUser();

    const { data: banners } = useBanners();
    const GLOBAL_ENGAGE_POPUP_BANNER_SESSION_KEY = "showGlobalEngagePopupBanner";

    const [isGlobalEngageModalOpen, setIsGlobalEngageModalOpen] = useState(true);
    const popupBanner = banners?.find(banner => banner.bannerType === 3);
    const showGlobalEngagePopupBanner = useMemo(() => {
        if (popupBanner) {
            if (popupBanner.isInternal && !currentUser?.isInternal) return false;
            const currentDate = new Date();
            const startDate = popupBanner.startDate ? new Date(popupBanner.startDate) : undefined;
            const endDate = popupBanner.endDate ? new Date(popupBanner.endDate) : undefined;

            if (!!startDate && currentDate >= startDate && !!endDate && currentDate <= endDate) {
                return true;
            }
        }
        return false;
    }, [popupBanner, currentUser]);

    const bannerProps: GlobalEngagePopupBannerProps = {
        title: popupBanner?.title ?? "",
        infoLinkText: popupBanner?.infoLinkText ?? "",
        description: popupBanner?.description ?? "",
        note: popupBanner?.note ?? "",
        engageLink: popupBanner?.engageLink ?? "",
        engageLinkText: popupBanner?.engageLinkText ?? "",
        supportingButtonLink: popupBanner?.videoLink ?? "",
        supportingButtonText: popupBanner?.videoLinkText ?? "",
    };

    useEffect(() => {
        if (showGlobalEngagePopupBanner) {
            sessionStorage.setItem(GLOBAL_ENGAGE_POPUP_BANNER_SESSION_KEY, "true");
            return;
        }

        sessionStorage.removeItem(GLOBAL_ENGAGE_POPUP_BANNER_SESSION_KEY);
    }, [showGlobalEngagePopupBanner]);

    const handleGlobalEngagePopupClose = () => {
        sessionStorage.removeItem(GLOBAL_ENGAGE_POPUP_BANNER_SESSION_KEY);
        setIsGlobalEngageModalOpen(false);
    };

    return (
        <>
            <ErrorBoundary location={location}>
                <CoreApplicationLayout appName={import.meta.env.VITE_APP_NAME} leftNav={<LeftNav />}>
                    <Suspense
                        fallback={
                            <Page title={""}>
                                <PageLoading text={"Loading Components..."} />
                            </Page>
                        }
                    >
                        {showGlobalEngagePopupBanner && (
                            <GlobalEngagePopupModal isOpen={isGlobalEngageModalOpen} onClose={handleGlobalEngagePopupClose} {...bannerProps} />
                        )}
                        <Outlet />
                    </Suspense>
                </CoreApplicationLayout>
            </ErrorBoundary>
            <ScrollToTop />
            <TrackNavigation location={location} />
        </>
    );
}

export default ApplicationLayout;
